import { Identify, identify, setUserId } from "@amplitude/analytics-browser";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { currentRetailerStorageKey, localStorageKey, retailersStorageKey, userSessionStorageKey } from "@core/constants";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { StorageMap } from "@ngx-pwa/local-storage";
import { combineLatest, forkJoin, of } from "rxjs";
import { catchError, delay, mergeMap, tap } from "rxjs/operators";

// Actions
import * as globalActions from "@core/store/actions/global.action";
import * as actions from "@modules/auth/store/actions/login.action";

// Services
import { CargoAuthService } from "@modules/auth/services/auth.service";

// Interfaces
import { HttpErrorResponse } from "@angular/common/http";
import { JwtSchema } from "@core/entities";
import { PopUpNotificationService } from "@core/services/popup-notification.service";
import { mapPermissions } from "@core/utils/map-permissions";
import { TranslateService } from "@ngx-translate/core";
import jwtDecode from "jwt-decode";
import { LOGIN_STAGE, OTPConfigResponse, PermissionGroupResponse, TWOFACTOR_TYPE } from "../../entities";

@Injectable()
export class CargoAuthEffects {
  constructor(
    private actions$: Actions,
    private authService: CargoAuthService,
    private storage: StorageMap,
    private router: Router,
    private translate: TranslateService,
    private popupService: PopUpNotificationService
  ) {}

  InitialAuthentication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.basicLogin),
      mergeMap(({ formdata }) =>
        this.authService.initialAuthentication(formdata).pipe(
          mergeMap((resp: { access_token: string; otp_configs: OTPConfigResponse[] }) => {
            const jwt: JwtSchema = jwtDecode(resp.access_token);
            const actionsResponse: any[] = [
              actions.setTwoFactorData({
                twofa: {
                  isTwoFactorAuthenticated: jwt.is_two_factor_authenticated,
                  isTwoFactorAuthenticationEnabled: jwt.is_two_factor_authentication_enabled,
                  requires2FAEnrollment: jwt.requires_2fa_enrollment,
                  twoFAOptOut: jwt.two_fa_opt_out,
                  preliminaryToken: resp.access_token,
                  otpConfig: resp.otp_configs,
                },
              }),
            ];
            if (jwt.two_fa_opt_out) {
              actionsResponse.push(
                actions.fetchRetailerIds({
                  resp: { access_token: resp.access_token },
                })
              );
            } else {
              actionsResponse.push(
                actions.uiAuth({
                  ui: [
                    {
                      name: "loginStage",
                      value: LOGIN_STAGE.TWOFACTOR_PROMPT,
                    },
                  ],
                })
              );
            }
            return actionsResponse;
          }),
          catchError((httpError: HttpErrorResponse) => {
            const { status, error } = httpError;
            console.error({ status, error });
            this.popupService.error(error);
            return of(actions.empty());
          })
        )
      )
    )
  );

  TwoFactorLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.twoFactorLogin),
      mergeMap(({ access_token, two_factor_authentication_code, twofactortype, fullHash, id }) =>
        this.authService.twoFactorTokenLogin(access_token, two_factor_authentication_code, twofactortype, id, fullHash).pipe(
          mergeMap((resp: { access_token: string }) => {
            return [actions.fetchRetailerIds({ resp }), actions.uiAuth({ ui: [{ name: "loadBtn", value: false }] })];
          }),
          catchError((httpError: HttpErrorResponse) => {
            const { status, error } = httpError;
            console.error({ status, error });
            this.popupService.error(error);
            return of(actions.empty());
          })
        )
      )
    )
  );

  TurnOnTwoFactorMethod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.turnOnTwoFactorAuthentication),
      mergeMap(({ method, otp, fullHash, accessToken, id }) =>
        this.authService.turnOnTwoFactorMethod(method, otp, accessToken, fullHash, id).pipe(
          mergeMap(() => {
            this.popupService.success({
              message: this.translate.instant("TWOFACTOR.SUCCESS_CONFIGURING_METHOD"),
            });
            return [
              actions.uiAuth({ ui: [{ name: "loadBtn", value: true }] }),
              actions.twoFactorLogin({
                access_token: accessToken,
                two_factor_authentication_code: otp,
                fullHash,
                twofactortype: fullHash ? TWOFACTOR_TYPE.PHONE : TWOFACTOR_TYPE.GOOGLE_AUTHENTICATOR,
                id,
              }),
            ];
          }),
          catchError((httpError: HttpErrorResponse) => {
            console.error({ httpError });
            if (httpError.status === 403) {
              this.popupService.error({
                message: this.translate.instant("ERRORS.INCORRECT_CODE"),
              });
            } else {
              this.popupService.error({
                message: this.translate.instant("ERRORS.OTHER"),
              });
            }
            return of(actions.empty());
          })
        )
      )
    )
  );

  SetupTwoFactorMethod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setupTwoFactorAuthentication),
      mergeMap(({ method, phone, accessToken, nickname }) =>
        this.authService.setupTwoFactorMethod(method, accessToken, phone, nickname).pipe(
          mergeMap((resp: { url?: string; fullHash?: string; id?: number }) => {
            const a: any[] = [
              actions.setTwoFactorData({
                twofa: {
                  qrImage: resp.url,
                  fullHash: resp.fullHash,
                  chosenMethod: {
                    type: resp.fullHash ? TWOFACTOR_TYPE.PHONE : TWOFACTOR_TYPE.GOOGLE_AUTHENTICATOR,
                    id: resp.id,
                    newMethod: true,
                  },
                },
              }),
            ];
            if (method === TWOFACTOR_TYPE.PHONE) {
              a.push(actions.uiAuth({ ui: [{ name: "loginStage", value: LOGIN_STAGE.PHONE_OTP_INPUT }] }));
            }
            return a;
          }),
          catchError((httpError: HttpErrorResponse) => {
            console.error({ httpError });
            return of(actions.empty());
          })
        )
      )
    )
  );

  SetupAdditionalTwoFactorMethod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setupAdditionalTwoFactorAuthentication),
      mergeMap(({ method, phone, nickname }) =>
        this.authService.setupAdditionalTwoFactorMethod(method, phone, nickname).pipe(
          mergeMap((resp: { url?: string; fullHash?: string; id?: number }) => {
            return [
              actions.setTwoFactorData({
                twofa: {
                  qrImage: resp.url,
                  fullHash: resp.fullHash,
                  chosenMethod: {
                    type: resp.fullHash ? TWOFACTOR_TYPE.PHONE : TWOFACTOR_TYPE.GOOGLE_AUTHENTICATOR,
                    id: resp.id,
                    newMethod: true,
                  },
                },
              }),
              actions.uiAuth({ ui: [{ name: "loginStage", value: LOGIN_STAGE.PHONE_OTP_INPUT }] }),
            ];
          }),
          catchError((httpError: HttpErrorResponse) => {
            console.error({ httpError });
            // this.popupService.error(error);
            return of(actions.empty());
          })
        )
      )
    )
  );

  TurnOnAdditionalTwoFactorMethod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.turnOnAdditionalTwoFactorAuthentication),
      mergeMap(({ method, otp, fullHash, authenticatorId }) =>
        this.authService.turnOnAdditionalTwoFactorMethod(method, otp, fullHash, authenticatorId).pipe(
          mergeMap((resp: { url?: string; fullHash?: string; id?: number }) => {
            this.popupService.success({
              message: this.translate.instant("TWOFACTOR.SUCCESS_CONFIGURING_METHOD"),
            });
            return [
              actions.listTwoFactorConfigs(),
              actions.setTwoFactorData({
                twofa: {
                  qrImage: resp.url,
                  chosenMethod: {
                    newMethod: true,
                    id: resp.id,
                    type: resp.fullHash ? TWOFACTOR_TYPE.PHONE : TWOFACTOR_TYPE.GOOGLE_AUTHENTICATOR,
                  },
                  fullHash: resp.fullHash,
                },
              }),
            ];
          }),
          catchError((httpError: HttpErrorResponse) => {
            console.error({ httpError });
            if (httpError.status === 403) {
              this.popupService.error({
                message: this.translate.instant("ERRORS.INCORRECT_CODE"),
              });
            } else {
              this.popupService.error({
                message: this.translate.instant("ERRORS.OTHER"),
              });
            }
            return of(actions.empty());
          })
        )
      )
    )
  );

  RequestPhoneOTP$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.requestPhoneOTP),
      mergeMap(({ id, accessToken }) =>
        this.authService.requestPhoneOTP(id, accessToken).pipe(
          mergeMap(({ fullHash }) => {
            return [actions.setTwoFactorData({ twofa: { fullHash } })];
          }),
          catchError((httpError: HttpErrorResponse) => {
            const { status, error } = httpError;
            console.error({ status, error });
            this.popupService.error(error);
            return of(actions.empty());
          })
        )
      )
    )
  );

  ListTwoFactorConfigs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.listTwoFactorConfigs),
      mergeMap(() =>
        this.authService.listTwoFactorConfigs().pipe(
          mergeMap(res => {
            return [actions.setTwoFactorData({ twofa: { otpConfig: res } })];
          }),
          catchError((httpError: HttpErrorResponse) => {
            const { status, error } = httpError;
            console.error({ status, error });
            this.popupService.error(error);
            return of(actions.empty());
          })
        )
      )
    )
  );

  DeleteConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteConfig),
      mergeMap(({ configId }) =>
        this.authService.deleteConfig(configId).pipe(
          mergeMap(() => {
            return [actions.listTwoFactorConfigs()];
          }),
          catchError((httpError: HttpErrorResponse) => {
            const { status, error } = httpError;
            console.error({ status, error });
            this.popupService.error(error);
            return of(actions.empty());
          })
        )
      )
    )
  );

  CreateRetailer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.createRetailer),
      mergeMap(({ payload }) =>
        this.authService.createRetailer(payload).pipe(
          mergeMap(() => {
            this.popupService.success({ message: "Cuenta creada exitosamente" });
            return [
              actions.uiAuth({
                ui: [{ name: "loadBtn", value: false }],
              }),
              actions.goLogin(),
            ];
          }),
          catchError((httpError: HttpErrorResponse) => {
            console.error({ httpError });
            const { error } = httpError;
            this.popupService.error(error);
            return of(actions.uiAuth({ ui: [{ name: "loadBtn", value: false }] }));
          })
        )
      )
    )
  );

  FetchRetailerIds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.fetchRetailerIds),
      mergeMap(({ resp }) => {
        return forkJoin([this.authService.fetchRetailerIds(resp), this.authService.fetchPermissionsGroup(resp)]).pipe(
          mergeMap(
            ([{ retailers }, { global_permissions, picking_point_permissions, retailer_permissions, ...pg }]: [
              any,
              PermissionGroupResponse
            ]) => {
              const permissionGroup = {
                permissions: mapPermissions(global_permissions, picking_point_permissions, retailer_permissions),
                ...pg,
              };
              const ans: any[] = [
                actions.wipeTwoFactorData(),
                actions.respSuccessAction({ payload: { auth: resp, permissionGroup, redirect: true } }),
                globalActions.setRetailers({ retailers }),
              ];
              if (retailers.length === 1) {
                ans.push(globalActions.getCurrentRetailer({ retailerId: retailers[0].id }));
              }
              return ans;
            }
          )
        );
      })
    )
  );

  PasswordReset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.passwordReset),
      mergeMap(({ payload }) => {
        return this.authService.passwordReset(payload).pipe(
          tap(() => {
            this.popupService.success({ message: this.translate.instant("AUTH.PASSWORD_RESET.SUCCESS") });
          }),
          delay(3000),
          tap(() => {
            this.router.navigate(["/auth/login"]);
          }),
          catchError(e => {
            this.popupService.error(e.error);
            return [];
          })
        );
      })
    )
  );

  RequestPasswordReset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.requestPasswordReset),
      mergeMap(({ email }) => {
        return this.authService.requestPasswordReset(email).pipe(
          mergeMap(() => {
            this.popupService.success({ message: this.translate.instant("AUTH.FORGOT_PASSWORD.SUCCESS") });
            return [actions.uiAuth({ ui: [{ name: "openResetPasswordModal", value: false }] })];
          }),
          catchError(_e => {
            this.popupService.error({ message: this.translate.instant("ERRORS.OTHER") });
            return [];
          })
        );
      })
    )
  );

  Auth$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.respSuccessAction),
        mergeMap(({ payload }) => {
          const jwt: JwtSchema = jwtDecode(payload.auth.access_token);
          setUserId(jwt.email + "");
          const identifyEvent = new Identify();
          identifyEvent.append("email", jwt.email);
          identifyEvent.append("name", jwt.name);
          identifyEvent.append("brand_id", jwt.brand_id);
          identify(identifyEvent);

          return combineLatest([
            this.storage.set(userSessionStorageKey, jwt.id),
            this.storage.set(localStorageKey, {
              ...payload.auth,
              permissionGroup: payload.permissionGroup,
              loading: false,
              authenticated: true,
              ...jwt,
            }),
          ]).pipe(
            tap(() => {
              if (payload.redirect) {
                this.router.navigate(["/dashboard/orders"]);
              }
            })
          );
        })
      ),
    { dispatch: false }
  );

  Fetch2FADeadline$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.fetch2FADeadline),
      mergeMap(() =>
        this.authService.fetch2FADeadline().pipe(
          mergeMap((resp: { cutoff_date: string; enabled: boolean }) => {
            return [
              actions.setTwoFactorData({
                twofa: {
                  deadline: resp.enabled && resp.cutoff_date ? resp.cutoff_date : null,
                },
              }),
            ];
          }),
          catchError((httpError: HttpErrorResponse) => {
            const { status, error } = httpError;
            console.error({ status, error });
            return of(actions.empty());
          })
        )
      )
    )
  );

  ExternalTokenLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.externalTokenLogin),
      mergeMap(({ access_token }) => {
        return forkJoin([
          this.storage.delete(localStorageKey),
          this.storage.delete(currentRetailerStorageKey),
          this.storage.delete(userSessionStorageKey),
          this.storage.delete(retailersStorageKey),
        ]).pipe(
          delay(3000),
          mergeMap(() => {
            return [actions.fetchRetailerIds({ resp: { access_token } })];
          })
        );
      })
    )
  );
}
